/**
 * RevisitQueuePanel — the doors worth going back to, on the canvasser's home
 * screen.
 *
 * `not_home` and `come_back` have been recorded on every shift since the
 * lifecycle model shipped and read back by nothing. A volunteer taps "Come
 * back" because somebody said *my husband's home after six*, and that sentence
 * has gone into a row nobody ever queries. Not-homes are usually most of a
 * shift's doors, so the campaign has been discarding the majority of its own
 * field effort at the moment it was collected. This panel is the read.
 *
 * Three rules it is built around
 * ------------------------------
 *
 * 1. **It never blocks the shift.** Canvassers now land here (App.tsx routes
 *    CANVASSER home to CanvassView), and the first thing on the screen has to
 *    stay "start walking". So this is a card below the start CTA, collapsed to
 *    a handful of rows, and it renders nothing at all while it is loading —
 *    a spinner that pushes the start button down the page is a worse trade
 *    than showing the queue a beat late. Never a modal, never a gate.
 *
 * 2. **It fetches for itself.** Unlike `SlateChip`, which is deliberately
 *    props-only because it has to survive a dead porch, this panel only ever
 *    renders before the walk, on the home screen, where there is signal. A
 *    failed fetch is a quiet one-line note rather than an error banner: the
 *    canvasser can still start a shift, and a red banner over a feature they
 *    did not ask for would train them to ignore banners.
 *
 * 3. **The count is the true backlog, not the page.** The server caps `doors`
 *    but returns real totals, and the headline reads from the totals. "Come
 *    back to 8 doors" has to mean eight doors.
 *
 * The server owns membership — latest-knock-wins, the DNC exclusion, the
 * staleness window, and the come-back-first ordering all live in
 * `backend/app/services/revisit_queue.py`. This component re-derives none of
 * it; it renders `doors` in the order it was handed them.
 */
import React, { useEffect, useState } from 'react';

import { formatCanvassError, getRevisitQueue } from '@/api/canvass';
import type { RevisitDoorRead, RevisitQueueResponse } from '@/types/canvass';

// How many rows show before "Show all". Five is about what fits on a 375px
// phone above the fold without pushing "Your recent shifts" off screen.
const COLLAPSED_ROWS = 5;

/**
 * "3 days ago" / "today". Days only — an exact clock time on a knock from last
 * week is precision the volunteer cannot use, and the hour they actually care
 * about ("after six") is in the notes, not in the timestamp.
 */
export function daysAgoLabel(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

/**
 * The headline sentence. Come-backs lead when there are any, because a person
 * asking us to return is a different kind of fact from nobody answering — and
 * burying it under a bigger not-home number is how the promise gets lost
 * again.
 */
export function headline(q: {
  come_back_count: number;
  not_home_count: number;
}): string {
  const { come_back_count: cb, not_home_count: nh } = q;
  if (cb > 0) return `Come back to ${cb} door${cb === 1 ? '' : 's'}`;
  if (nh > 0) return `${nh} door${nh === 1 ? '' : 's'} nobody was home for`;
  return 'Nothing to go back to yet';
}

function ReasonBadge({ reason }: { reason: RevisitDoorRead['reason'] }) {
  const asked = reason === 'come_back';
  return (
    <span
      className={
        'text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0 ' +
        (asked
          ? 'bg-accent/15 text-accent'
          : 'bg-bg-elevated text-fg-muted')
      }
    >
      {/* Plain words, not the enum. A volunteer should not have to learn that
          "come_back" is a thing the database says. */}
      {asked ? 'Asked us back' : 'Nobody home'}
    </span>
  );
}

function DoorRow({
  door,
  onStartRoute,
}: {
  door: RevisitDoorRead;
  onStartRoute?: (routeId: number) => void;
}) {
  return (
    <li className="py-2 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <ReasonBadge reason={door.reason} />
          <span className="text-fg-primary text-sm font-medium truncate">
            {door.voter_name}
          </span>
        </div>
        <div className="text-xs text-fg-muted mt-0.5 truncate">
          {door.address}
          {door.route_name ? ` · ${door.route_name}` : ''} ·{' '}
          {daysAgoLabel(door.last_knocked_at)}
        </div>
        {/* The volunteer's own words, verbatim. There is no callback-time
            column in the schema, so "after six" only survives if we show the
            sentence somebody typed. Never parsed, never summarized. */}
        {door.last_notes && (
          <div className="text-xs text-fg-secondary mt-0.5 italic break-words">
            “{door.last_notes}”
          </div>
        )}
      </div>
      {onStartRoute && (
        <button
          onClick={() => onStartRoute(door.route_id)}
          className="bg-bg-elevated hover:bg-border-subtle text-fg-primary text-xs font-semibold px-3 py-1 rounded shrink-0 inline-flex items-center justify-center min-h-11 sm:min-h-0"
        >
          Walk it →
        </button>
      )}
    </li>
  );
}

export function RevisitQueuePanel({
  onStartRoute,
}: {
  /** Start a walk-up shift on this door's route. Omit to render read-only. */
  onStartRoute?: (routeId: number) => void;
}) {
  const [queue, setQueue] = useState<RevisitQueueResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getRevisitQueue()
      .then((q) => {
        if (!cancelled) setQueue(q);
      })
      .catch((e) => {
        if (!cancelled) setError(formatCanvassError(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Rule 1: nothing on screen until we have something to say. The start-a-
  // shift CTA above must not move while this loads.
  if (!queue && !error) return null;

  if (error) {
    return (
      <div
        className="bg-bg-raised rounded-lg border border-border-base p-3 text-xs text-fg-muted"
        data-testid="revisit-queue-error"
      >
        Could not load your revisit queue. {error}
      </div>
    );
  }

  const q = queue as RevisitQueueResponse;
  const shown = expanded ? q.doors : q.doors.slice(0, COLLAPSED_ROWS);
  // `total` is the whole eligible set; `doors.length` is the capped page. The
  // difference is real and the footer says so rather than implying the list is
  // everything.
  const notListed = q.total - q.doors.length;

  return (
    <div
      className="bg-bg-raised rounded-lg border border-border-base p-4"
      data-testid="revisit-queue"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-fg-secondary uppercase tracking-wide">
            Go back
          </h3>
          <p className="text-fg-primary font-bold mt-1">{headline(q)}</p>
          {q.total > 0 && (
            <p className="text-xs text-fg-muted mt-0.5">
              {q.come_back_count > 0 && q.not_home_count > 0
                ? `Plus ${q.not_home_count} nobody was home for. `
                : ''}
              Knocked in the last {q.window_days} days.
            </p>
          )}
        </div>
      </div>

      {q.total === 0 ? (
        <p className="text-fg-muted text-sm italic mt-2">
          Come-backs and not-homes from your routes show up here.
        </p>
      ) : (
        <>
          <ul className="divide-y divide-border-base mt-2">
            {shown.map((d) => (
              <DoorRow
                key={`${d.route_stop_id}-${d.voter_id}`}
                door={d}
                onStartRoute={onStartRoute}
              />
            ))}
          </ul>
          {q.doors.length > COLLAPSED_ROWS && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 text-xs font-semibold text-accent hover:underline"
            >
              {expanded
                ? 'Show fewer'
                : `Show all ${q.doors.length}`}
            </button>
          )}
          {notListed > 0 && (
            <p className="text-[11px] text-fg-muted mt-2">
              {notListed} more not listed — work these first, or ask a
              coordinator to re-route the street.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default RevisitQueuePanel;
